/**
 * Google Drive Preloader Service
 *
 * Handles background preloading of Google Drive data when the app loads
 * to improve user experience by having data ready before they navigate to Drive sections
 */

import type { CacheKey } from "@/lib/contexts/GoogleDriveCache/types";
import type {
  PreloadConfig,
  PreloadResult,
  PreloadStatus,
  PreloadTask,
} from "./google-drive-preloader.types";
import { Semaphore } from "./google-drive-preloader.semaphore";
import { checkAccountAvailability, executePreloadTask } from "./google-drive-preloader.fetcher";
import { logger } from "@/lib/logger";

export type { PreloadConfig, PreloadResult, PreloadStatus } from "./google-drive-preloader.types";

export class GoogleDrivePreloader {
  private config: PreloadConfig;
  private abortController: AbortController | null = null;
  private status: PreloadStatus = {
    isPreloading: false,
    completed: 0,
    total: 0,
    results: [],
  };

  constructor(config: Partial<PreloadConfig> = {}) {
    this.config = {
      enabled: true,
      preloadSharedDrives: true,
      preloadRootFolders: true,
      maxConcurrentRequests: 2, // Conservative to avoid rate limits
      delayBetweenRequests: 500, // 500ms delay between requests
      timeoutPerRequest: 10000, // 10 second timeout per request
      ...config,
    };
  }

  async startPreload(
    accessToken: string,
    cacheCallback: (key: CacheKey, data: any) => void,
    statusCallback?: (status: PreloadStatus) => void
  ): Promise<PreloadStatus> {
    if (!this.config.enabled || this.status.isPreloading) {
      return this.status;
    }

    logger.info("🚀 Google Drive Preloader: Starting background preload...");

    this.abortController = new AbortController();
    this.status = {
      isPreloading: true,
      completed: 0,
      total: 0,
      results: [],
      startTime: Date.now(),
    };

    try {
      const preloadTasks = await this.buildPreloadTasks(accessToken);
      this.status.total = preloadTasks.length;

      logger.info(`🚀 Google Drive Preloader: ${preloadTasks.length} tasks queued`);

      await this.executePreloadTasks(preloadTasks, accessToken, cacheCallback, statusCallback);

      this.status.endTime = Date.now();
      this.status.isPreloading = false;

      const duration = this.status.endTime - (this.status.startTime || 0);
      const successCount = this.status.results.filter((r) => r.success).length;

      logger.info(`🚀 Google Drive Preloader: Completed in ${duration}ms`, {
        total: this.status.total,
        successful: successCount,
        failed: this.status.total - successCount,
      });
    } catch (error: any) {
      logger.error("🚀 Google Drive Preloader: Error during preload", error);
      this.status.isPreloading = false;
      this.status.endTime = Date.now();
    }

    return this.status;
  }

  stopPreload(): void {
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }
    this.status.isPreloading = false;
  }

  getStatus(): PreloadStatus {
    return { ...this.status };
  }

  updateConfig(config: Partial<PreloadConfig>): void {
    this.config = { ...this.config, ...config };
  }

  private async buildPreloadTasks(accessToken: string): Promise<PreloadTask[]> {
    const tasks: PreloadTask[] = [];

    try {
      const accountInfo = await checkAccountAvailability(
        accessToken,
        this.abortController?.signal
      );

      if (this.config.preloadSharedDrives) {
        if (accountInfo.primaryAvailable) {
          tasks.push({
            cacheKey: { action: "drives", isSecondAccount: false },
            priority: 1,
            description: "Primary account shared drives",
          });
        }

        if (accountInfo.secondaryAvailable) {
          tasks.push({
            cacheKey: { action: "drives", isSecondAccount: true },
            priority: 1,
            description: "Secondary account shared drives",
          });
        }
      }

      if (this.config.preloadRootFolders) {
        if (accountInfo.primaryAvailable) {
          tasks.push({
            cacheKey: { action: "list", isSecondAccount: false },
            priority: 2,
            description: "Primary account root folder",
          });
        }

        if (accountInfo.secondaryAvailable) {
          tasks.push({
            cacheKey: { action: "list", isSecondAccount: true },
            priority: 2,
            description: "Secondary account root folder",
          });
        }
      }

      tasks.sort((a, b) => a.priority - b.priority);
    } catch (error) {
      logger.warn("🚀 Google Drive Preloader: Error building preload tasks", { error });
    }

    return tasks;
  }

  private async executePreloadTasks(
    tasks: PreloadTask[],
    accessToken: string,
    cacheCallback: (key: CacheKey, data: any) => void,
    statusCallback?: (status: PreloadStatus) => void
  ): Promise<void> {
    const semaphore = new Semaphore(this.config.maxConcurrentRequests);

    const executeTask = async (task: PreloadTask): Promise<void> => {
      await semaphore.acquire();

      try {
        await this.delay(this.config.delayBetweenRequests);
        const result = await executePreloadTask(
          task,
          accessToken,
          this.config,
          this.abortController?.signal
        );

        this.status.results.push(result);
        this.status.completed++;

        if (result.success && result.data) {
          cacheCallback(result.cacheKey, result.data);
        }

        statusCallback?.(this.getStatus());
      } finally {
        semaphore.release();
      }
    };

    await Promise.allSettled(tasks.map(executeTask));
  }

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }
}
